import Select from "sap/m/Select";
import { MetadataOptions } from "sap/ui/core/Element";
import Control from "sap/ui/core/Control";
import List from "sap/m/List";
import ManagedObject from "sap/ui/base/ManagedObject";

/**
 * @namespace ips.ac.control
 */

interface ColorEntry {
  bg: string;
  fg: string;
}

type ColorPalette = Record<string, ColorEntry>;

const DEFAULT_COLOR: ColorEntry = { bg: "#eeeeee", fg: "#333333" };

function normalizePalette(pal: ColorPalette | null): ColorPalette {
  return pal || { DEFAULT: DEFAULT_COLOR };
}

/**
 * @namespace ips.ac.control
 */
export default class ColorSelect extends Select {
  static readonly metadata: MetadataOptions = {
    properties: {
      palette: { type: "object", defaultValue: null },
    },
  };

  static renderer = "sap.m.SelectRenderer";

  private _bPickerHooked = false;

  init(): void {
    super.init();
    this.addStyleClass("ipsColorSelect");
    this.attachChange(this._applyClosedColors, this);
  }

  // @ts-ignore
  createPicker(pickerType: string): Control {
    const picker = super.createPicker(pickerType);

    if (!this._bPickerHooked) {
      this._bPickerHooked = true;

      if (picker?.attachEvent) {
        picker.attachEvent("beforeOpen", this._onOpen, this);
      }

      const content = picker?.getAggregation("content") as ManagedObject[];
      const list = content?.[0] as List;
      if (list?.addEventDelegate) {
        list.addEventDelegate({
          onAfterRendering: this._onOpen.bind(this),
        });
      }
    }

    return picker;
  }

  setSelectedKey(sKey: string): this {
    super.setSelectedKey(sKey);
    this._applyClosedColors();
    return this;
  }

  setPalette(palette: ColorPalette | null): this {
    this.setProperty("palette", palette, true);
    this._applyClosedColors();
    return this;
  }

  onAfterRendering(event: jQuery.Event): void {
    super.onAfterRendering(event);
    this._applyClosedColors();
  }

  private _getColorsForKey(sKey: string | null): ColorEntry {
    const pal = normalizePalette(this.getProperty("palette") as ColorPalette | null);
    const k = sKey == null ? "DEFAULT" : String(sKey).trim().toUpperCase();
    return pal[k] || pal.DEFAULT || DEFAULT_COLOR;
  }

  private _applyClosedColors(): void {
    if (!this.getDomRef()) return;
    const { bg, fg } = this._getColorsForKey(this.getSelectedKey());
    const $el = this.$();
    // @ts-ignore
    $el.css("--ips-cs-bg", bg);
    // @ts-ignore
    $el.css("--ips-cs-fg", fg);
  }

  private _onOpen(): void {
    const oPicker = this.getAggregation("picker") as any;
    if (!oPicker) return;

    const oList = oPicker.getContent?.()?.[0];
    if (!oList) return;

    this.getItems().forEach((oItem) => {
      const { bg, fg } = this._getColorsForKey(oItem.getKey());
      const $li = oItem.$() as jQuery;
      // @ts-ignore
      $li.css("background-color", bg);
      // @ts-ignore
      $li.css("color", fg);
    });
  }
}
